<template>
    <BaseEdge :id="id" :style="edgeStyle" :path="path[0]" :marker-end="markerEnd" :class="debugEdgeClasses" @mouseenter="handleEdgeEnter" @mouseleave="handleEdgeLeave" />

    <EdgeLabelRenderer>
        <div v-if="!lockFlowCreatorStatus && !isViewMode" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave" :style="{
            pointerEvents: showDeleteButton ? 'all' : 'none',
            opacity: showDeleteButton ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${path[1]}px,${path[2]}px)`,
        }" class="nodrag nopan">
            <button class="edgebutton" @click="handleRemoveEdge"><v-icon size="16">mdi-close</v-icon></button>
        </div>
    </EdgeLabelRenderer>
</template>

<script>
import { BaseEdge, EdgeLabelRenderer, getBezierPath, useVueFlow } from '@vue-flow/core'

export default {
    inheritAttrs: false,
    components: {
        BaseEdge,
        EdgeLabelRenderer
    },
    inject: ['lockFlowCreatorStatus', 'isDebugMode', 'debugEdgeStates'],
    props: {
        id: {
            type: String,
            required: true,
        },
        sourceX: {
            type: Number,
            required: true,
        },
        sourceY: {
            type: Number,
            required: true,
        },
        targetX: {
            type: Number,
            required: true,
        },
        targetY: {
            type: Number,
            required: true,
        },
        sourcePosition: {
            type: String,
            required: true,
        },
        targetPosition: {
            type: String,
            required: true,
        },
        markerEnd: {
            type: String,
            required: false,
        },
        style: {
            type: Object,
            required: false,
        },
        source: {
            type: String,
            required: false,
        },
        target: {
            type: String,
            required: false,
        },
        sourceHandleId: {
            type: String,
            required: false,
        },
        targetHandleId: {
            type: String,
            required: false,
        },
        data: {
            type: Object,
            required: false,
        }
    },
    setup(props) {
        const vueFlowInstance = useVueFlow();
        const { removeEdges } = vueFlowInstance;
        
        const handleRemoveEdge = () => {
            removeEdges(props.id);
        };
        
        return { 
            handleRemoveEdge, 
            vueFlowInstance
        };
    },
    data() {
        return {
            isEdgeHovered: false,
            mouseOverButton: false,
        };
    },
    mounted() {
        // console.log(this.lockFlowCreatorStatus)  // จะได้ true/false
    },
    computed: {
        showDeleteButton() {
            return this.isEdgeHovered || this.mouseOverButton;
        },
        isViewMode() {
            return this.$route?.query?.mode === 'view';
        },
        pathCoordinates() {
            const isJsonConditionOutput = this.sourceHandleId === 'output-match' || this.sourceHandleId === 'output-fallback';

            return {
                sourceX: isJsonConditionOutput ? this.sourceX - 8 : this.sourceX,
                sourceY: this.sourceY,
                targetX: this.targetX,
                targetY: this.targetY,
            };
        },
        isParallelOrMergeEdge() {
            try {
                const targetNode = this.findFlowNode(this.target);
                const sourceNode = this.findFlowNode(this.source);
                const isTargetMerge = this.isNodeType(targetNode, 'merge');
                const isParallelSource = this.getParallelSourceStatus(sourceNode);

                return isParallelSource || isTargetMerge;
            } catch (error) {
                console.error(error);
                // Node lookup failed; treat as non-parallel/non-merge edge (default styling).
                return false;
            }
        },
        edgeStyle() {
            const baseStyle = {
                strokeWidth: 1.5,
                ...this.style
            };
            
            // ถ้าเป็น parallel หรือ merge edge ให้ใช้เส้นประ (dashed)
            if (this.isParallelOrMergeEdge) {
                return {
                    ...baseStyle,
                    strokeDasharray: '8,5'  // ใช้ '2,2' สำหรับจุด (dotted)
                };
            }
            
            return baseStyle;
        },
        path() {
            const edgePathProps = {
                ...this,
                ...this.pathCoordinates,
            };

            // Knowledge nodes ที่ต้องใช้ Bezier curve แทน SmoothStep
            const knowledgeNodeTypes = new Set(['documents', 'databaseIntegration', 'products', 'intentAI', 'apiknowledge', 'sharepoint', 'knowledgeGraph', 'nexaWaterfallSearch']);
            
            try {
                // ตรวจสอบว่า target node เป็น Knowledge node หรือไม่
                const targetNode = this.vueFlowInstance.nodes.value.find(node => node.id === this.target);
                const isTargetKnowledgeNode = targetNode && knowledgeNodeTypes.has(targetNode.data?.type);
                
                // ตรวจสอบว่า source node เป็น Knowledge node หรือไม่
                const sourceNode = this.vueFlowInstance.nodes.value.find(node => node.id === this.source);
                const isSourceKnowledgeNode = sourceNode && knowledgeNodeTypes.has(sourceNode.data?.type);
                
                // ถ้า source หรือ target เป็น Knowledge node ให้ใช้ Bezier curve
                if (isTargetKnowledgeNode || isSourceKnowledgeNode) {
                    return getBezierPath(this);
                }
                
                // ตรวจสอบว่าเป็น parallel connection หรือไม่
                // 1. Parent AI Model (isParallel=true) -> child AI Model/Merge
                // 2. Any node -> Merge node
                // const isSourceModel = sourceNode && sourceNode.data?.type === 'model';
                // const isTargetModel = targetNode && targetNode.data?.type === 'model';
                // const isTargetMerge = targetNode && targetNode.data?.type === 'merge';
                // const isParallelSource = this.data?.isParallel === true;
                
                // ถ้าเป็น parallel connection หรือไปหา Merge ให้ใช้ SmoothStep (เส้นประ)
                // if (isParallelSource || isTargetMerge) {
                //     return getSmoothStepPath(this);
                // }
            } catch (error) {
                console.log("Error accessing nodes:", error);
                // ถ้า error ให้ fallback ไปใช้ logic เดิม
            }
            
            // Keep standard connections visually consistent on both left and right placement.
            return getBezierPath(edgePathProps);
        },
        debugEdgeClasses() {
            if (!this.isDebugMode) return {};
            const state = this.debugEdgeStates[this.id];
            return {
                'edge-active': state === 'active',
                'edge-completed': state === 'completed',
            };
        },
    },
    methods: {
        findFlowNode(nodeId) {
            return this.vueFlowInstance.nodes.value.find(node => node.id === nodeId);
        },
        isNodeType(node, type) {
            return node && node.data?.type === type;
        },
        isIntentNode(node) {
            return this.isNodeType(node, 'intent') || this.isNodeType(node, 'smartIntent');
        },
        getEdgesFromSourceHandle() {
            return this.vueFlowInstance.edges.value.filter(e =>
                e.source === this.source && e.sourceHandle === this.sourceHandleId
            );
        },
        getParallelSourceStatus(sourceNode) {
            if (this.isNodeType(sourceNode, 'model') && this.sourceHandleId === 'output-bottom') {
                return false;
            }

            const isParallelSource = this.data?.isParallel === true;
            if (!this.isIntentNode(sourceNode)) {
                return isParallelSource;
            }

            const edgesFromHandle = this.getEdgesFromSourceHandle();
            return edgesFromHandle.length >= 2;
        },
        handleEdgeEnter() {
            this.isEdgeHovered = true;
        },
        handleEdgeLeave() {
            this.isEdgeHovered = false;
        },
        handleMouseEnter() {
            this.mouseOverButton = true;
        },
        handleMouseLeave() {
            this.mouseOverButton = false;
        }
    }
}
</script>

<style scoped>
.edge-button-container {
    opacity: 1;
    transition: opacity 0.3s ease-in-out;
    z-index: 10;
    /* Added z-index to bring the button on top */
}

.edgebutton {
    width: 24px;
    height: 24px;
    background-color: #c15151;
    border: 2px solid #ffffff;
    border-radius: 50%;
    color: white;
    font-size: 14px;
    font-weight: bold;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    transition: all 0.2s ease-in-out;
    line-height: 1;
    padding: 0;
    opacity: 1;
    pointer-events: all;
}

.edgebutton:hover {
    background-color: #cf4343;
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

.edgebutton:active {
    transform: scale(0.95);
}

/* ============================================
   DEBUG EDGE STYLES
   ============================================ */
.edge-active .vue-flow__edge-path {
    stroke: #42a5f5 !important;
    stroke-width: 2.5px;
    stroke-dasharray: 8 4;
    animation: edgeDash 0.5s linear infinite;
}
.edge-completed .vue-flow__edge-path {
    stroke: #4caf50 !important;
    stroke-width: 2px;
}
@keyframes edgeDash {
    to { stroke-dashoffset: -24; }
}
</style>